import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'shared/components/alerts/use-alert';
import { useUpdateWorkerAvailability } from './use-update-worker-availability';
import { useCreateAvailability } from './use-create-availability';
import { AvailabilityMode } from './use-availability-action-handler';
import { SegmentsForEdit, TimeSlotFormContext } from '../wizard/time-slot.types';
import { isOverlappingSegment } from '../utils/is-overlapping-segment';

type Props = {
  workerId: string | undefined;
  originalAvailability: SegmentsForEdit[];
  availabilityDates: DateTime[];
  fromDate: DateTime;
  searchTerm: string | undefined;
  cancel: () => void;
  mode?: AvailabilityMode;
};

export const useHandleAvailabilitySubmission = ({
  workerId,
  originalAvailability,
  availabilityDates,
  fromDate,
  searchTerm,
  cancel,
  mode = 'edit',
}: Props) => {
  const { t } = useTranslation('all-platforms-common');
  const isCreateMode = mode === 'create';
  const { alertSuccess, alertError } = useAlert();
  const {
    handleSubmit: formHandleSubmit,
    watch,
    setError,
    clearErrors,
  } = useFormContext<TimeSlotFormContext>();
  const availabilitySegments = watch('availability');

  const { updateWorkerAvailability } = useUpdateWorkerAvailability(
    workerId ?? '',
    fromDate,
    searchTerm,
    cancel
  );
  const { createAvailabilityMutation } = useCreateAvailability(searchTerm, fromDate, 'ogp');

  const validateAndSetErrors = useCallback(
    (data: TimeSlotFormContext) => {
      clearErrors();
      const overlappingIndex = data.availability.findIndex((segment) =>
        isOverlappingSegment(segment, [...data.availability, ...originalAvailability])
      );

      const internallyInvalidIndex = data.availability.findIndex(
        (segment) =>
          DateTime.fromFormat(segment.utcStartDateTime, 'T') >=
          DateTime.fromFormat(segment.utcEndDateTime, 'T')
      );

      if (internallyInvalidIndex !== -1) {
        setError(`availability.${internallyInvalidIndex}.utcStartDateTime`, {
          message: t('edit_availability.timeslot_invalid'),
        });
        setError(`availability.${internallyInvalidIndex}.utcEndDateTime`, {
          message: t('edit_availability.timeslot_invalid'),
        });
        return false;
      }

      if (overlappingIndex !== -1) {
        setError(`availability.${overlappingIndex}.utcStartDateTime`, {
          message: t('edit_availability.timeslot_overlap'),
        });
        setError(`availability.${overlappingIndex}.utcEndDateTime`, {
          message: t('edit_availability.timeslot_overlap'),
        });
        return false;
      }

      if (isCreateMode) {
        const segmentsToCreate = data.availability.filter(
          (segment) => segment.utcStartDateTime && segment.utcEndDateTime
        );
        if (segmentsToCreate.length === 0) {
          setError('availability', {
            message:
              t('edit_availability.timeslot_required') || 'Please add at least one time slot',
          });

          return false;
        }
        if (availabilityDates.length === 0) {
          setError('availability', {
            message: t('edit_availability.date_required.error_message', {
              defaultValue: 'Please select at least one date',
            }),
          });

          return false;
        }
      }

      return true;
    },
    [originalAvailability.length, availabilityDates.length, isCreateMode]
  );

  const handleSubmit = formHandleSubmit(async (data: TimeSlotFormContext) => {
    if (!validateAndSetErrors(data)) {
      return;
    }

    try {
      if (isCreateMode) {
        const segmentsToCreate = data.availability
          .filter((segment) => segment.utcStartDateTime && segment.utcEndDateTime)
          .map((segment) => ({
            startDateTime: DateTime.fromFormat(segment.utcStartDateTime, 'T').toISO(),
            endDateTime: DateTime.fromFormat(segment.utcEndDateTime, 'T').toISO(),
          }));

        const selectedWorker = data.workerSelect;
        if (!selectedWorker) {
          setError('workerSelect', {
            message: t('edit_availability.worker_required'),
          });
          return;
        }

        await createAvailabilityMutation({
          availabilityDates: availabilityDates.map((date) => date.toISO()),
          workerId: selectedWorker,
          updatedSegments: [],
          removedSegmentIds: [],
          newSegments: segmentsToCreate,
        });

        alertSuccess(t('edit_availability.create_success'));
        cancel();
      } else {
        updateWorkerAvailability({
          availabilityDates: availabilityDates.map((date) => date.toISO()),
          updatedSegments: data.availability.map((segment) => ({
            segmentId: segment.segmentId,
            startDateTime: DateTime.fromFormat(segment.utcStartDateTime, 'T').toISO(),
            endDateTime: DateTime.fromFormat(segment.utcEndDateTime, 'T').toISO(),
          })),
          newSegments: [],
          removedSegmentIds: originalAvailability
            .filter((segment) => !data.availability.find((s) => s.segmentId === segment.segmentId))
            .map((segment) => segment.segmentId),
        });
      }
    } catch (e) {
      alertError(e);
    }
  });

  const handleDeleteAll = formHandleSubmit(() => {
    if (availabilityDates.length !== 1) {
      throw new Error('Invalid number of availability dates');
    }

    updateWorkerAvailability({
      availabilityDates: availabilityDates.map((date) => date.toISO()),
      updatedSegments: [],
      removedSegmentIds: availabilitySegments.map((segment) => segment.segmentId),
      newSegments: [],
    });
  });

  return {
    handleSubmit,
    handleDeleteAll,
  };
};
